if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');

        this.selectors = {
          variantIdSelect: '[name="id"]',
        };

        this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
        this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get quantity() {
        return this.querySelector('quantity-input');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get select() {
        return this.querySelector('[data-sticky-variant-select]') || this.querySelector('select');
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      connectedCallback() {
        this.productFormActions = document.querySelector(`.main-product-form[data-product-id="${this.productId}"]`);
        this.mainProductInfo = document.querySelector(`product-info[data-product-id="${this.productId}"]`);
        this.container = this.closest('.sticky-atc-bar');
        this.submitButton = this.querySelector('[type="submit"]');
        this.selectedVariantId = this.querySelector(this.selectors.variantIdSelect)?.value;

        this.variantData = this.getVariantData();

        this.initVariantDropdown();
        this.init();

        this.select?.addEventListener('change', () => {
          if (this.isUpdating) return;

          this.selectedVariantId = this.select.value;
          this.syncVariantDropdown();
          this.isUpdating = true;

          this.updateQuantityInput();

          this.mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
          const selectedVariantId = this.querySelector(this.selectors.variantIdSelect)?.value;
          this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));

          if (!this.currentVariant) {
            this.isUpdating = false;
            this.updateButton(true, '', true);
            return;
          }

          if (this.mainVariantSelects) {
            Array.from(this.mainVariantSelects.querySelectorAll('select, fieldset'), (element, index) => {
              const variantOptionVal = this.currentVariant.options[index];

              switch (element.tagName) {
                case 'SELECT': {
                  element.value = variantOptionVal;
                  const options = element.querySelectorAll('option');
                  options.forEach((option) => option.removeAttribute('selected'));

                  const selectedOption = element.querySelector(`option[value="${CSS.escape(variantOptionVal)}"]`);
                  if (selectedOption) {
                    selectedOption.setAttribute('selected', 'selected');
                  }
                  break;
                }

                case 'FIELDSET':
                  Array.from(element.querySelectorAll('input')).forEach((radio) => {
                    if (radio.value === variantOptionVal) {
                      radio.checked = true;
                    }
                  });
                  break;
              }
            });

            setTimeout(() => {
              this.mainVariantSelects.dispatchEvent(new Event('change', { detail: { formStickty: true } }));
              this.isUpdating = false;
            }, 0);
          } else {
            this.isUpdating = false;
          }

          this.updatePrice();
          this.updateButton(!this.currentVariant.available, FoxTheme.variantStrings.soldOut);
        });

        const hasRequiredFields = this.validateMainProductRequiredFields();
        if (hasRequiredFields && this.submitButton && this.productFormActions) {
          this.submitButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.productFormActions.scrollIntoView({ behavior: 'smooth', block: 'center' });

            setTimeout(() => {
              this.productFormActions.requestSubmit();
            }, 300);
          });
        }
      }

      disconnectedCallback() {
        document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
        document.removeEventListener('keydown', this.handleDocumentKeydown);
        this.observer?.disconnect();
      }

      initVariantDropdown() {
        this.variantDropdown = this.querySelector('[data-sticky-variant-dropdown]');
        this.variantTrigger = this.querySelector('[data-sticky-variant-trigger]');
        this.variantMenu = this.querySelector('[data-sticky-variant-menu]');
        this.variantCurrent = this.querySelector('[data-sticky-variant-current]');
        this.variantOptions = Array.from(this.querySelectorAll('[data-sticky-variant-option]'));

        if (!this.variantDropdown || !this.variantTrigger || !this.variantMenu || !this.select) {
          return;
        }

        this.variantTrigger.addEventListener('click', () => {
          this.toggleVariantDropdown();
        });

        this.variantTrigger.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

          event.preventDefault();
          this.openVariantDropdown();

          const selectedIndex = this.getSelectedVariantOptionIndex();
          const fallbackIndex = event.key === 'ArrowDown' ? 0 : this.variantOptions.length - 1;
          this.focusVariantOption(selectedIndex >= 0 ? selectedIndex : fallbackIndex);
        });

        this.variantOptions.forEach((option, index) => {
          option.addEventListener('click', () => {
            this.selectVariantFromDropdown(option.dataset.value);
          });

          option.addEventListener('keydown', (event) => {
            this.handleVariantOptionKeydown(event, index);
          });
        });

        document.addEventListener('pointerdown', this.handleDocumentPointerDown);
        document.addEventListener('keydown', this.handleDocumentKeydown);

        this.syncVariantDropdown();
      }

      toggleVariantDropdown() {
        if (this.variantDropdown?.classList.contains('is-open')) {
          this.closeVariantDropdown();
        } else {
          this.openVariantDropdown();
        }
      }

      openVariantDropdown() {
        if (!this.variantDropdown || !this.variantMenu || !this.variantTrigger) return;

        this.variantDropdown.classList.add('is-open');
        this.variantMenu.hidden = false;
        this.variantTrigger.setAttribute('aria-expanded', 'true');
      }

      closeVariantDropdown({ focusTrigger = false } = {}) {
        if (!this.variantDropdown || !this.variantMenu || !this.variantTrigger) return;

        this.variantDropdown.classList.remove('is-open');
        this.variantMenu.hidden = true;
        this.variantTrigger.setAttribute('aria-expanded', 'false');

        if (focusTrigger) {
          this.variantTrigger.focus();
        }
      }

      selectVariantFromDropdown(value) {
        if (!value || !this.select) return;

        if (this.select.value === value) {
          this.closeVariantDropdown({ focusTrigger: true });
          return;
        }

        this.select.value = value;
        this.selectedVariantId = value;
        this.syncVariantDropdown();
        this.closeVariantDropdown({ focusTrigger: true });
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      syncVariantDropdown() {
        if (!this.select) return;

        const selectedValue = String(this.select.value);
        const selectedNativeOption = this.select.options?.[this.select.selectedIndex];

        if (this.variantCurrent && selectedNativeOption) {
          this.variantCurrent.textContent = selectedNativeOption.textContent.trim();
        }

        this.variantOptions?.forEach((option) => {
          const isSelected = String(option.dataset.value) === selectedValue;
          option.classList.toggle('is-selected', isSelected);
          option.setAttribute('aria-selected', String(isSelected));
        });
      }

      getSelectedVariantOptionIndex() {
        if (!this.select || !this.variantOptions?.length) return -1;

        return this.variantOptions.findIndex(
          (option) => String(option.dataset.value) === String(this.select.value)
        );
      }

      focusVariantOption(index) {
        if (!this.variantOptions?.length) return;

        const normalizedIndex = (index + this.variantOptions.length) % this.variantOptions.length;
        this.variantOptions[normalizedIndex]?.focus();
      }

      handleVariantOptionKeydown(event, index) {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            this.focusVariantOption(index + 1);
            break;

          case 'ArrowUp':
            event.preventDefault();
            this.focusVariantOption(index - 1);
            break;

          case 'Home':
            event.preventDefault();
            this.focusVariantOption(0);
            break;

          case 'End':
            event.preventDefault();
            this.focusVariantOption(this.variantOptions.length - 1);
            break;

          case 'Escape':
            event.preventDefault();
            this.closeVariantDropdown({ focusTrigger: true });
            break;
        }
      }

      handleDocumentPointerDown(event) {
        if (!this.variantDropdown?.classList.contains('is-open')) return;
        if (this.variantDropdown.contains(event.target)) return;

        this.closeVariantDropdown();
      }

      handleDocumentKeydown(event) {
        if (event.key !== 'Escape') return;
        if (!this.variantDropdown?.classList.contains('is-open')) return;

        event.preventDefault();
        this.closeVariantDropdown({ focusTrigger: true });
      }

      validateMainProductRequiredFields() {
        const mainForm = this.mainProductInfo;
        if (!mainForm) return true;
        const requiredFields = mainForm.querySelectorAll('[data-custom-property]');
        return requiredFields.length > 0;
      }

      updateQuantityInput() {
        const selectedVariantId = this.select?.value || this.selectedVariantId;
        this.selectedVariantId = selectedVariantId;
        this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));

        if (!this.currentVariant) return;

        fetch(`${this.getAttribute('data-url')}?variant=${this.currentVariant.id}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const quantity = document.querySelector(`#QuantitySticky-${this.sectionId}`);
            const newQuantity = parsedHTML.querySelector(`#QuantitySticky-${this.sectionId}`);

            if (quantity && newQuantity) {
              quantity.innerHTML = newQuantity.innerHTML;
            }
          })
          .catch((error) => {
            console.error(error);
          });
      }

      getVariantData() {
        this.variantData =
          this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      init() {
        if (!this.productFormActions) {
          this.container.classList.add('sticky-atc-bar--show');
          return;
        }

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.checkDevice.bind(this);
        this.checkDevice();

        const rootMargin = '-80px 0px 0px 0px';
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const method = entry.intersectionRatio !== 1 ? 'add' : 'remove';
              this.container.classList[method]('sticky-atc-bar--show');
            });
          },
          { threshold: 1, rootMargin }
        );

        this.setObserveTarget();
        this.syncWithMainProductForm();
      }

      setObserveTarget() {
        this.observer.observe(this.productFormActions);
        this.observeTarget = this.productFormActions;
      }

      checkDevice() {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
      }

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('#product-form-sticky-atc-bar');
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }
      }

      updatePrice() {
        if (!this.currentVariant) return;

        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );

        const unitPrice = unitPriceWrapper?.querySelector('.f-price__unit');
        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        if (compare_at_price && compare_at_price > price) {
          priceWrapper?.classList.add(classes.onSale);
        } else {
          priceWrapper?.classList.remove(classes.onSale);
        }

        if (!this.currentVariant.available) {
          priceWrapper?.classList.add(classes.soldOut);
        } else {
          priceWrapper?.classList.remove(classes.soldOut);
        }

        if (salePrice) {
          salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);
        }

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat))
          );
        } else if (compareAtPrice) {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            this.currentVariant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${FoxTheme.Currency.getBaseUnit(this.currentVariant)}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper?.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          if (!this.mainProductInfo) return;

          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;

          const variant = event.data.variant;
          const variantInput = this.querySelector('[name="id"]');

          this.currentVariant = variant;

          if (!variant) {
            this.updateButton(true, '', true);
            return;
          }

          if (variantInput) {
            variantInput.value = variant.id;
          }

          this.selectedVariantId = String(variant.id);
          this.syncVariantDropdown();
          this.updatePrice();
          this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          this.updateQuantityInput();
        });
      }
    }
  );
}
